import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export function PlayerPanel() {
  const { user, isAuthenticated } = useAuth();

  const { data: stats } = useQuery({
    queryKey: ['player-stats', user?.id],
    queryFn: () => user ? api.getPlayerStats(user.id) : null,
    enabled: !!user,
  });

  if (!isAuthenticated) {
    return (
      <div className="card text-center py-12">
        <div className="text-4xl mb-4">👤</div>
        <h2 className="text-xl font-bold mb-2">Login Required</h2>
        <p className="text-lunar-400 mb-4">Sign in to view your profile and stats</p>
      </div>
    );
  }

  const statsData = stats as Record<string, unknown>;
  const player = statsData?.player as Record<string, unknown>;
  const playerStats = statsData?.stats as Record<string, unknown>;
  const aoeTotals = (statsData?.aoe_totals as Array<Record<string, unknown>>) || [];
  const achievements = (statsData?.recent_achievements as Array<Record<string, unknown>>) || [];

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="text-4xl">{(player?.avatar as string) || '👤'}</div>
          <div>
            <h2 className="text-xl font-bold">{(player?.display_name as string) || 'Unknown'}</h2>
            <div className="text-sm text-lunar-400">
              🏆 {(playerStats?.current_rank as string) || 'Rookie'} • 
              ⚡ {(player?.total_impact_points as number)?.toLocaleString() || 0} impact points
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-mars-400">
            {(playerStats?.total_games_played as number) || 0}
          </div>
          <div className="text-xs text-lunar-300">Games Played</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-mars-400">
            {(playerStats?.total_high_scores as number) || 0}
          </div>
          <div className="text-xs text-lunar-300">High Scores</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-mars-400">
            {(playerStats?.aoe_categories as number) || 0}
          </div>
          <div className="text-xs text-lunar-300">AOE Categories</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-mars-400">
            {(playerStats?.achievements as number) || 0}
          </div>
          <div className="text-xs text-lunar-300">Achievements</div>
        </div>
      </div>

      {/* AOE Totals */}
      <div className="card">
        <h3 className="font-bold mb-3">📊 Areas of Expertise</h3>
        <div className="space-y-2">
          {aoeTotals.map((aoe) => (
            <div key={aoe.aoe_type as string} className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{aoe.aoe_type as string}</span>
                  <span className="text-mars-400">{(aoe.lifetime_points as number)?.toLocaleString()}</span>
                </div>
                <div className="bg-lunar-700 rounded-full h-2">
                  <div 
                    className="bg-mars-400 rounded-full h-2 transition-all"
                    style={{ width: `${Math.min(100, ((aoe.lifetime_points as number || 0) / 10000) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
          {aoeTotals.length === 0 && (
            <div className="text-center text-lunar-400 py-4">
              No AOE scores yet. Play some games!
            </div>
          )}
        </div>
      </div>

      {/* Recent Achievements */}
      <div className="card">
        <h3 className="font-bold mb-3">🏅 Recent Achievements</h3>
        <div className="space-y-2">
          {achievements.map((achievement) => (
            <div 
              key={achievement.id as number}
              className="flex items-center gap-3 bg-lunar-700 rounded-lg p-3"
            >
              <div className="text-2xl">
                {(achievement.tier_badge as string) || '🏆'}
              </div>
              <div>
                <div className="font-medium" style={{ color: String(achievement.tier_color || '#fff') }}>
                  {achievement.tier_name as string}
                </div>
                <div className="text-xs text-lunar-400">
                  {achievement.achievement_type === 'personal_rank' ? 'Personal Rank' : 'AOE Rank'}
                  {achievement.aoe_type ? ` • ${String(achievement.aoe_type)}` : ''}
                </div>
              </div>
            </div>
          ))}
          {achievements.length === 0 && (
            <div className="text-center text-lunar-400 py-4">
              No achievements yet. Keep playing!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
